/**
 * Analytics Tracking Utility
 * Centralized tracking for user actions
 *
 * Phase 4 — server-side sink. Every `trackEvent` call ALSO POSTs to
 * `/api/analytics/event` so the admin dashboard can aggregate real
 * traffic, funnel, and revenue-intelligence metrics. Send is
 * fire-and-forget with `keepalive: true` so it survives page unloads
 * exactly like GA4 does.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ==================================================================
// Session + UTM tracking (Phase 4)
// ==================================================================
// Session id — 30-day sticky UUID stored in localStorage. Everything
// downstream ties to it (funnel, uniques, abandon).
function _readSessionId() {
  try {
    let s = localStorage.getItem('travelai_sid');
    if (!s) {
      s = 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('travelai_sid', s);
    }
    return s;
  } catch { return null; }
}

// UTM capture — persist on FIRST landing this session. Never overwrite.
// If none present, fall back to `document.referrer` host so we can
// separate organic search / social / direct without paid campaigns.
function _readAttribution() {
  try {
    const cached = sessionStorage.getItem('travelai_attr');
    if (cached) return JSON.parse(cached);
    const p = new URLSearchParams(window.location.search || '');
    const attr = {
      utm_source:   p.get('utm_source')   || null,
      utm_medium:   p.get('utm_medium')   || null,
      utm_campaign: p.get('utm_campaign') || null,
      referrer_host: (() => {
        try {
          const r = document.referrer || '';
          if (!r) return null;
          const host = new URL(r).host;
          const self = window.location.host;
          return host && host !== self ? host : null;
        } catch { return null; }
      })(),
    };
    sessionStorage.setItem('travelai_attr', JSON.stringify(attr));
    return attr;
  } catch {
    return { utm_source: null, utm_medium: null, utm_campaign: null, referrer_host: null };
  }
}

function _readLanguage() {
  try {
    return localStorage.getItem('userLanguage') || null;
  } catch { return null; }
}

function _readUserId() {
  try {
    // Auth writes this in the login flow; empty when logged out.
    return localStorage.getItem('travelai_user_id') || null;
  } catch { return null; }
}

async function _serverIngest(eventName, eventData) {
  if (!API_BASE) return;
  try {
    const attr = _readAttribution();
    const body = JSON.stringify({
      event_type:   eventName,
      session_id:   _readSessionId() || 'sid_anon',
      user_id:      _readUserId(),
      language:     _readLanguage(),
      utm_source:   attr.utm_source,
      utm_medium:   attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      referrer_host: attr.referrer_host,
      payload:       eventData || {},
    });
    // Prefer sendBeacon on unload paths; fetch(keepalive) works everywhere else.
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        `${API_BASE}/api/analytics/event`,
        new Blob([body], { type: 'application/json' }),
      );
      if (ok) return;
    }
    // Fire-and-forget. Never throw. Never block.
    fetch(`${API_BASE}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  } catch { /* analytics must never break the UI */ }
}

/**
 * Track user events for analytics
 * @param {string} eventName - Name of the event
 * @param {object} eventData - Additional event data
 */
export function trackEvent(eventName, eventData = {}) {
  // One Google Analytics dispatch per action. gtag itself writes into
  // dataLayer, so also pushing a GTM event here can count it twice.
  let gaDispatched = false;
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventData);
      gaDispatched = true;
    }
  } catch { /* GA outages must not disrupt booking navigation */ }

  // Sites that install only GTM (without gtag) can still consume events.
  if (!gaDispatched && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
      timestamp: new Date().toISOString()
    });
  }

  // Phase-4 server sink
  _serverIngest(eventName, eventData);

  // Console log for development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics]', eventName, eventData);
  }
}

/**
 * Track search event
 * @param {object} searchParams - Search parameters
 */
export function trackSearch(searchParams) {
  trackEvent('search', {
    type: searchParams.type || 'general', // 'flight', 'hotel', 'package'
    destination: searchParams.destination || searchParams.city,
    origin: searchParams.origin,
    dates: searchParams.dates || `${searchParams.checkin}-${searchParams.checkout}`,
    guests: searchParams.guests || searchParams.adults,
    budget_type: searchParams.budget_type
  });
}

/**
 * Track hotel click
 * @param {object} hotel - Hotel object
 */
export function trackHotelClick(hotel) {
  trackEvent('hotel_click', {
    hotel_id: hotel.id,
    hotel_name: hotel.name,
    hotel_price: hotel.price,
    hotel_rating: hotel.rating,
    hotel_location: hotel.location || hotel.city
  });
}

/**
 * Track flight click
 * @param {object} flight - Flight object
 */
export function trackFlightClick(flight) {
  trackEvent('flight_click', {
    airline: flight.airline,
    route: `${flight.departure} - ${flight.arrival}`,
    price: flight.price,
    type: flight.type
  });
}

/**
 * Track booking initiation
 * @param {object} bookingData - Booking details
 */
export function trackBookingClick(bookingData) {
  trackEvent('booking_click', {
    type: bookingData.type, // 'hotel', 'flight', 'package'
    item_id: bookingData.id,
    item_name: bookingData.name,
    price: bookingData.price,
    currency: bookingData.currency || 'EUR'
  });
}

/**
 * Track AI chat message
 * @param {string} userMessage - User's message
 * @param {string} intent - Detected intent
 */
export function trackAIChat(userMessage, intent) {
  trackEvent('ai_chat_message', {
    message_length: userMessage.length,
    intent: intent,
    has_destination: /paris|london|rome|barcelona/i.test(userMessage),
    has_budget: /cheap|budget|expensive|luxury|\€|\$/i.test(userMessage)
  });
}

/**
 * Track package view
 * @param {object} packageData - Package details
 */
export function trackPackageView(packageData) {
  trackEvent('package_view', {
    destination: packageData.destination,
    total_price: packageData.totalPrice,
    package_type: packageData.type,
    includes_flight: !!packageData.flight,
    includes_hotel: !!packageData.hotel
  });
}

/**
 * Track successful booking
 * @param {object} bookingDetails - Final booking info
 */
export function trackBookingComplete(bookingDetails) {
  trackEvent('booking_complete', {
    booking_id: bookingDetails.bookingId,
    total_amount: bookingDetails.totalAmount,
    currency: bookingDetails.currency,
    type: bookingDetails.type
  });
}

// =============================================================
// Phase 2 — Conversion & Trust event schema
// =============================================================
// These are the KPI-critical events that power the Phase-2 mandate:
// affiliate CTR, session completion, booking probability. Every new
// event goes through the same `trackEvent` sink above, so gtag +
// dataLayer + dev console see them for free. Keep payload keys stable
// so downstream GA4 dashboards do not break.
// =============================================================

/**
 * Track any Call-to-Action click (primary or secondary button/link).
 * @param {object} payload
 * @param {string} payload.cta_id       Stable identifier — matches data-testid where possible.
 * @param {string} payload.screen        Which surface fired it ('home', 'chat', 'flights', ...).
 * @param {string} [payload.variant]     'primary' | 'secondary' | 'tertiary'.
 * @param {string} [payload.destination] Optional — where the click leads next.
 */
export function trackCTAClick({ cta_id, screen, variant = 'primary', destination = null, ...extra } = {}) {
  trackEvent('cta_click', { cta_id, screen, variant, destination, ...extra });
}

/**
 * Track a click on any content card (deal, ToolCard, package, hotel/flight tile).
 * @param {object} payload
 * @param {string} payload.card_type   'flight_deal' | 'hotel_card' | 'flight_card' | 'package_card' | 'destination_card' | 'hidden_gem'
 * @param {string} payload.item_id     Stable id of the card (route, hotel_id, package_slug, iata, ...)
 * @param {string} payload.screen      'home' | 'chat' | 'flights' | 'hotels' | 'packages' | 'destination' | ...
 * @param {number} [payload.position]  0-based index in the surrounding list.
 * @param {string[]} [payload.reason_chips]  Chips shown on the card at click time (Cheapest, Fastest, ...).
 */
export function trackCardClick({ card_type, item_id, screen, position = null, reason_chips = null, ...extra } = {}) {
  trackEvent('card_click', { card_type, item_id, screen, position, reason_chips, ...extra });
}

/**
 * Track an outbound affiliate redirect. Fired IMMEDIATELY BEFORE navigating
 * the browser to the partner (Aviasales, Booking, DiscoverCars, Kiwitaxi).
 * This is the single event that lets us compute affiliate CTR + revenue
 * attribution — it MUST fire on every outbound partner click.
 *
 * @param {object} payload
 * @param {string} payload.partner        'aviasales' | 'booking' | 'discover_cars' | 'kiwitaxi' | ...
 * @param {string} payload.type           'flight' | 'hotel' | 'car' | 'taxi' | 'ferry' | 'train'
 * @param {string} [payload.origin]       Origin IATA / city (flights, cars, taxi).
 * @param {string} [payload.destination]  Destination IATA / city.
 * @param {number} [payload.price]        Displayed price (integer, EUR).
 * @param {string} [payload.marker]       Travelpayouts marker in the URL (audit trail).
 * @param {string} [payload.surface]      Where the click came from — 'chat' | 'flights' | 'hotels' | 'home' | ...
 * @param {string[]} [payload.reason_chips]  Reasoning shown on the card at click time.
 */
export function trackAffiliateRedirect({
  partner, type, origin = null, destination = null, price = null,
  marker = null, surface = null, reason_chips = null, ...extra
} = {}) {
  trackEvent('affiliate_redirect', {
    partner, type, origin, destination, price, marker, surface, reason_chips, ...extra,
  });
}

/**
 * Homepage global-search submission (Hero + TravelBookingSection).
 */
export function trackHomepageSearch({ query, city_iata = null, mode = 'flight', ...extra } = {}) {
  trackEvent('homepage_search', { query, city_iata, mode, ...extra });
}

/**
 * User has started a trip planning session — first AI chat message OR
 * first flight/hotel search on the site. Fire ONCE per session.
 */
let _tripStartFired = false;
export function trackTripStart({ surface, ...extra } = {}) {
  if (_tripStartFired) return;
  _tripStartFired = true;
  trackEvent('trip_start', { surface, ...extra });
}

/**
 * User reached a state where they clicked a partner Book CTA and were
 * redirected — considered a completed intent for our funnel.
 */
export function trackTripCompletion({ surface, destination = null, ...extra } = {}) {
  trackEvent('trip_completion', { surface, destination, ...extra });
}

/**
 * Fired when the user has been idle for 30s on a results page. Set up
 * once at mount via `setupAbandonWatcher(screen)`; the watcher tears
 * itself down on unmount OR on any user interaction that resets the
 * timer. Payload includes the last action taken.
 */
export function trackSessionAbandon({ screen, last_action = null, ...extra } = {}) {
  trackEvent('session_abandon', { screen, last_action, ...extra });
}

/**
 * Convenience — set up + return a cleanup fn for a 30s idle watcher on
 * a given screen. Any of the standard interaction events (click, keydown,
 * touchstart, scroll) resets the timer.
 */
export function setupAbandonWatcher(screen, { idleMs = 30000, getLastAction = () => null } = {}) {
  if (typeof window === 'undefined') return () => {};
  let timer = null;
  const fire = () => {
    trackSessionAbandon({ screen, last_action: getLastAction() });
  };
  const reset = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fire, idleMs);
  };
  const events = ['click', 'keydown', 'touchstart', 'scroll'];
  events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
  reset();
  return () => {
    if (timer) clearTimeout(timer);
    events.forEach((e) => window.removeEventListener(e, reset));
  };
}

// Make trackEvent available globally for backward compatibility
if (typeof window !== 'undefined') {
  window.trackEvent = trackEvent;
}
